import * as React from 'react';
import { useState } from 'react';
import { Card, CardContent, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useRecordContext, useTranslate, useNotify, useDataProvider, useRefresh } from 'react-admin';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Order } from '../types';
import { useTheme } from '@mui/material/styles';

export const AsideFraudWarning = () => {
    const translate = useTranslate();
    const record = useRecordContext<Order>();
    const theme = useTheme();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    // Only display if lowRisk is false
    if (!record || record.lowRisk !== false) {
        return null;
    }

    const openConfirmDialog = () => {
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setConfirmDialogOpen(false);
    };

    const handleConfirmOrder = async () => {
        closeConfirmDialog();
        setLoading(true);
        try {
            await dataProvider.update('orders', {
                id: record.id,
                data: { lowRisk: true },
                previousData: record,
            });
            notify('resources.order.fraud_warning.confirm_success', { type: 'success' });
            refresh();
        } catch (error) {
            notify('resources.order.fraud_warning.confirm_error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box mt={{xs: '20px', md: '0'}} ml={{xs: '0', md: '20px'}} width={{xs: '100%', md: '90%'}}>
            <Card
                sx={{
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.error.contrastText,
                    marginTop: 2,
                }}
            >
                <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                        <ErrorOutlineIcon sx={{ marginRight: 1 }} />
                        <Typography variant="h6" component="div">
                            {translate('resources.order.fraud_warning.title')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                        {translate('resources.order.fraud_warning.content')}
                    </Typography>

                    <Box display="flex" justifyContent="center" width="100%">
                        <Button
                            variant="contained"
                            size="small"
                            color="inherit"
                            onClick={openConfirmDialog}
                            disabled={loading}
                            fullWidth
                            sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                        >
                            {translate('resources.order.fraud_warning.confirm_button')}
                        </Button>
                    </Box>

                    <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                        <DialogTitle>
                            {translate('resources.order.fraud_warning.title')}
                        </DialogTitle>
                        <DialogContent>
                            <Typography>
                                {translate('resources.order.fraud_warning.dialog_content')}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeConfirmDialog} color="inherit">
                                {translate('ra.action.cancel')}
                            </Button>
                            <Button onClick={handleConfirmOrder} color="primary" variant="contained" autoFocus>
                                {translate('ra.action.confirm')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CardContent>
            </Card>
        </Box>
    );
};
